/**
 * Collection infinite scroll (progressive enhancement).
 *
 * Keeps classic ?page= pagination in the HTML for SEO / no-JS.
 * When JS runs, fetches the next page via the Section Rendering API
 * and appends product cards. URL stays in sync with history.replaceState.
 * The load-more sentinel is hidden once every product is on the page.
 */
window.CollectionInfiniteScroll = (function () {
  var observer = null;
  var loading = false;

  function afterAppend() {
    if (typeof setWishlistButtonsState === 'function') setWishlistButtonsState();
    if (typeof runCurrencyConverter === 'function') runCurrencyConverter();
  }

  function forceLazyImages(node) {
    node.querySelectorAll('img').forEach(function (img) {
      img.loading = 'lazy';
      img.removeAttribute('fetchpriority');
    });
  }

  function init() {
    var root = document.querySelector('[data-collection-infinite]');
    if (!root) return;

    var grid = root.querySelector('[data-collection-grid]');
    var sentinel = root.querySelector('[data-collection-sentinel]');
    if (!grid || !sentinel) return;

    if (observer) {
      observer.disconnect();
      observer = null;
    }

    var currentPage = parseInt(root.dataset.currentPage, 10) || 1;
    var totalPages = parseInt(root.dataset.totalPages, 10) || 1;
    var sectionId = root.dataset.sectionId;
    var productsCount = parseInt(root.dataset.productsCount, 10);
    var pageSize = parseInt(root.dataset.pageSize, 10);

    function cardCount() {
      return grid.querySelectorAll('.collection-card').length;
    }

    function reachedEnd(lastBatchSize) {
      if (currentPage >= totalPages) return true;
      if (!isNaN(productsCount) && productsCount >= 0 && cardCount() >= productsCount) return true;
      if (!isNaN(pageSize) && pageSize > 0 && typeof lastBatchSize === 'number' && lastBatchSize < pageSize) {
        return true;
      }
      return false;
    }

    function hideSentinel() {
      sentinel.hidden = true;
      sentinel.classList.remove('is-loading');
      sentinel.setAttribute('aria-busy', 'false');
      if (observer) {
        observer.disconnect();
        observer = null;
      }
    }

    if (!sectionId || reachedEnd()) {
      hideSentinel();
      return;
    }

    root.setAttribute('data-infinite-enabled', '');
    sentinel.hidden = false;
    loading = false;

    function syncUrl(page) {
      var url = new URL(window.location.href);
      if (page <= 1) {
        url.searchParams.delete('page');
      } else {
        url.searchParams.set('page', String(page));
      }
      url.searchParams.delete('section_id');
      url.searchParams.delete('sections');
      history.replaceState(null, '', url.pathname + url.search + url.hash);
    }

    async function loadNext() {
      if (reachedEnd()) {
        hideSentinel();
        return;
      }
      if (loading) return;

      loading = true;
      sentinel.setAttribute('aria-busy', 'true');
      sentinel.classList.add('is-loading');

      var nextPage = currentPage + 1;
      var url = new URL(window.location.pathname, window.location.origin);

      new URL(window.location.href).searchParams.forEach(function (value, key) {
        if (key === 'section_id' || key === 'sections' || key === 'page') return;
        url.searchParams.append(key, value);
      });
      url.searchParams.set('page', String(nextPage));
      url.searchParams.set('section_id', sectionId);

      try {
        var res = await fetch(url.toString(), { headers: { Accept: 'text/html' } });
        if (!res.ok) throw new Error('Collection page fetch failed');

        var html = await res.text();
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var nextGrid = doc.querySelector('[data-collection-grid]');
        if (!nextGrid) throw new Error('No collection grid in response');

        var cards = Array.prototype.slice.call(nextGrid.querySelectorAll('.collection-card'));
        if (!cards.length) {
          currentPage = totalPages;
          root.dataset.currentPage = String(currentPage);
          hideSentinel();
          return;
        }

        var fragment = document.createDocumentFragment();
        cards.forEach(function (card) {
          forceLazyImages(card);
          fragment.appendChild(document.importNode(card, true));
        });
        grid.appendChild(fragment);

        currentPage = nextPage;
        root.dataset.currentPage = String(currentPage);
        syncUrl(currentPage);
        afterAppend();

        if (reachedEnd(cards.length)) hideSentinel();
      } catch (err) {
        console.warn('[collection-infinite-scroll]', err);
      } finally {
        loading = false;
        if (!sentinel.hidden) {
          sentinel.setAttribute('aria-busy', 'false');
          sentinel.classList.remove('is-loading');
        }
      }
    }

    observer = new IntersectionObserver(
      function (entries) {
        if (entries.some(function (entry) {
          return entry.isIntersecting;
        })) {
          loadNext();
        }
      },
      { rootMargin: '400px 0px' }
    );

    observer.observe(sentinel);

    var loadMoreBtn = sentinel.querySelector('[data-collection-load-more]');
    if (loadMoreBtn) {
      loadMoreBtn.addEventListener('click', function () {
        loadNext();
      });
    }
  }

  document.addEventListener('DOMContentLoaded', init);

  return { init: init };
})();
